import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { IMAGES } from "@/assets/images";
import { supabase } from "@/integrations/supabase/client";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";
import { useLanguage } from "@/lib/language";

export default function Contact() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Zapis w bazie danych
      const { error: dbError } = await supabase
        .from('contact_submissions_20260303')
        .insert({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message
        });
      
      if (dbError) {
        console.error('Database error:', dbError);
        setSubmitStatus("error");
        setIsSubmitting(false);
        return;
      }
      
      // Wysłanie powiadomienia Telegram
      const { error: telegramError } = await supabase.functions.invoke('telegram_notifications_20260304', {
        body: {
          type: 'contact',
          data: formData
        }
      });
      
      if (telegramError) {
        console.error('Telegram error:', telegramError);
      }
      
      setSubmitStatus("success");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const contactInfo = [
    {
      icon: Phone,
      title: t('contact.info.phone.title') + ' (Polska)',
      value: "+48 22 263 91 91",
      description: t('contact.info.phone.description')
    },
    {
      icon: Phone,
      title: t('contact.info.phone.title') + ' (Szwajcaria)',
      value: "+41 415 331 634",
      description: t('contact.info.phone.description')
    },
    {
      icon: Mail,
      title: t('contact.info.email.title'),
      value: "support@expresshub.pro",
      description: t('contact.info.email.description')
    },
    {
      icon: MapPin,
      title: t('contact.info.address.title'),
      value: "Via Vite, 6, Stabio",
      description: t('contact.info.address.description')
    },
    {
      icon: Clock,
      title: t('contact.info.hours.title'),
      value: t('workingHours.weekdays'),
      description: t('contact.info.hours.description')
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.EXPRESS_HUB_OFFICE}
            alt="EXPRESS HUB Office"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Informacje kontaktowe */}
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="space-y-8"
              >
                <motion.div variants={staggerItem}>
                  <h2 className="text-2xl font-bold mb-6">{t('contact.info.phone.title')}</h2>
                </motion.div>

                <div className="grid gap-6">
                  {contactInfo.map((info, index) => (
                    <motion.div
                      key={index}
                      variants={staggerItem}
                      className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 flex-shrink-0">
                        <info.icon className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1">{info.title}</h3>
                        <p className="text-foreground mb-1">{info.value}</p>
                        <p className="text-sm text-muted-foreground">{info.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Formularz kontaktowy */}
              <motion.div
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={springPresets.gentle}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      {t('contact.form.title')}
                    </CardTitle>
                    <CardDescription>
                      {t('contact.subtitle')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {submitStatus === "success" ? (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                          <Send className="w-8 h-8 text-green-600" />
                        </div>
                        <h3 className="text-lg font-semibold mb-2">{t('contact.form.success')}</h3>
                        <p className="text-muted-foreground mb-4">
                          {t('contact.info.email.description')}
                        </p>
                        <Button onClick={() => setSubmitStatus("idle")}>
                          {t('common.back')}
                        </Button>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="name" className="block text-sm font-medium mb-2">
                              {t('contact.form.name')} *
                            </label>
                            <Input 
                              id="name" 
                              name="name" 
                              type="text" 
                              value={formData.name} 
                              onChange={handleChange} 
                              placeholder={t('common.name')} 
                              required 
                              className="border-border/50" 
                            />
                          </div>
                          <div>
                            <label htmlFor="phone" className="block text-sm font-medium mb-2">
                              {t('common.phone')}
                            </label>
                            <Input 
                              id="phone" 
                              name="phone" 
                              type="tel" 
                              value={formData.phone} 
                              onChange={handleChange} 
                              placeholder="+48 22 263 91 91" 
                              className="border-border/50" 
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium mb-2">
                            {t('contact.form.email')} *
                          </label>
                          <Input 
                            id="email" 
                            name="email" 
                            type="email" 
                            value={formData.email} 
                            onChange={handleChange} 
                            placeholder="email@example.com" 
                            required 
                            className="border-border/50" 
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="message" className="block text-sm font-medium mb-2">
                            {t('contact.form.message')} *
                          </label>
                          <Textarea 
                            id="message" 
                            name="message" 
                            value={formData.message} 
                            onChange={handleChange} 
                            placeholder={t('contact.form.message')} 
                            required 
                            rows={5} 
                            className="border-border/50" 
                          />
                        </div>
                        
                        <Button 
                          type="submit" 
                          className="w-full" 
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
                        </Button>

                        {submitStatus === "error" && (
                          <div className="text-center text-destructive text-sm mt-4">
                            {t('contact.form.error')}
                          </div>
                        )}
                      </form>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('services.ctaTitle')}
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              {t('about.contact.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <a href="tel:+48222639191">
                  <Phone className="w-4 h-4 mr-2" />
                  {t('contact.callNow')} (PL): +48 22 263 91 91
                </a>
              </Button>
              <Button size="lg" asChild>
                <a href="tel:+41415331634">
                  <Phone className="w-4 h-4 mr-2" />
                  {t('contact.callNow')} (CH): +41 415 331 634
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="mailto:support@expresshub.pro">
                  <Mail className="w-4 h-4 mr-2" />
                  {t('tracking.emailButton')}
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}